import { Card } from "@devhop/ui";
import { memo } from "react";
import type { NodeProps } from "reactflow";
import { Handle, Position } from "reactflow";

export interface TrainingFlowStepNodeData {
  step: {
    id: string;
    title: string;
    description: string | null;
    stepNumber: number;
    stepTypeName: string | null;
    positionName: string | null;
    departmentName: string | null;
  };
}

function plainPreview(html: string | null): string | null {
  if (!html?.trim()) return null;
  const t = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return t || null;
}

function getStepTypeColor(stepTypeName: string | null): string {
  if (!stepTypeName) {
    return "bg-gray-50 border-gray-200 text-gray-800";
  }
  const type = stepTypeName.toLowerCase();
  if (type.includes("ວຽກ") || type.includes("work")) {
    return "bg-blue-50 border-blue-200 text-blue-800";
  }
  if (type.includes("ກວດສອບ") || type.includes("check")) {
    return "bg-yellow-50 border-yellow-200 text-yellow-800";
  }
  if (type.includes("ອະນຸມັດ") || type.includes("approve")) {
    return "bg-green-50 border-green-200 text-green-800";
  }
  return "bg-gray-50 border-gray-200 text-gray-800";
}

export const TrainingFlowStepNode = memo(
  ({ data }: NodeProps<TrainingFlowStepNodeData>) => {
    const { step } = data;
    const typeColor = getStepTypeColor(step.stepTypeName);
    const preview = plainPreview(step.description);

    return (
      <div className="relative">
        <Handle
          type="target"
          position={Position.Left}
          id="left"
          className="h-2 w-2 border border-white bg-gray-400"
        />
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          className="h-2 w-2 border border-white bg-gray-400"
        />

        <Card
          className={`w-48 cursor-pointer gap-0 border p-3 transition-colors hover:shadow-md ${typeColor}`}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="font-semibold text-muted-foreground text-xs">
              #{step.stepNumber}
            </span>
            {step.stepTypeName ? (
              <span className="font-medium text-xs">{step.stepTypeName}</span>
            ) : null}
          </div>

          <h3 className="mb-2 font-semibold text-sm leading-tight">{step.title}</h3>

          {preview ? (
            <p className="mb-2 line-clamp-2 text-muted-foreground text-xs">{preview}</p>
          ) : null}

          {step.positionName ? (
            <div className="mb-1 text-xs">
              <span className="font-medium">ຜູ້ຮັບຜິດຊອບ: </span>
              <span className="text-muted-foreground">{step.positionName}</span>
            </div>
          ) : null}

          {step.departmentName ? (
            <div className="text-xs">
              <span className="font-medium">ພະແນກ: </span>
              <span className="text-muted-foreground">{step.departmentName}</span>
            </div>
          ) : null}
        </Card>

        <Handle
          type="source"
          position={Position.Right}
          id="right"
          className="h-2 w-2 border border-white bg-gray-400"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className="h-2 w-2 border border-white bg-gray-400"
        />
      </div>
    );
  },
);

TrainingFlowStepNode.displayName = "TrainingFlowStepNode";
